import { useState, useEffect, useCallback } from 'preact/hooks'
import { apiClient, authStore, InitData, assetUrl } from '../../core'
import { BookingModal, type BookableClass } from '../../components/BookingModal'
import { intlLocale, formatMoneyParts } from '../../core/i18n'
import { t } from '../../core/i18n'
import { fmtDag, fmtTid, studietsTidszone } from '../../core/tidszone'
import { parseSpotsDisplay, widgetSpotsText } from '../../core/spots-display'
import { ExpandableDesc, expandableDescStyles } from '../../components/ExpandableDesc'

interface ClassType {
  id: string
  name: string
  description: string | null
  imageUrl: string | null
  duration: number | null
  durationMinutes?: number | null
  level: string | null
  maxParticipants?: number | null
  benefits?: string[] | null
  price: number | null
  instructorName?: string | null
}

/** One scheduled class of a given type, as returned by /api/widget/classes. */
interface TypeSession {
  id: string
  name: string
  startTime: string
  endTime: string
  instructorName?: string | null
  room?: string | null
  description?: string | null
  maxParticipants: number
  currentParticipants: number
  dropInPriceDkk: number
  waitlistEnabled?: boolean
  membershipRequired?: boolean
  excludeFromMembership?: boolean
  /** «Gratis hold» (OPG-0098): ingen betaling, aldrig et klip. */
  isFree?: boolean
  clipCost?: number
  classTemplateId?: string | null
  guestsAllowed?: boolean
  isBooked?: boolean
}

interface Props {
  initData: InitData
  title?: string | null
  maxItems?: number | null
  showBenefits?: boolean
  showMeta?: boolean
  holdTypeSlugs?: string[]
}

/**
 * ClassTypesWidget — mirrors HS-native class template cards.
 *
 * Card layout:
 *   - Optional 16:9 image at top
 *   - Title (bold, 1.3rem)
 *   - Meta line: duration / level / max participants
 *   - Description (3 lines, «Læs mere» folds out the full text with line breaks)
 *   - Benefits list with orange ✓ (max 4)
 *   - Price (prominent) + outlined "Book hold" CTA
 */
export function ClassTypesWidget({ initData, title = null, maxItems = null, showBenefits = true, showMeta = true, holdTypeSlugs = [] }: Props) {
  // Pulled out here because the render below shadows `t` with its map parameter.
  const str = t(initData.locale)
  const dateLocale = intlLocale(initData.locale)
  const tz = studietsTidszone(initData.timezone)
  // «Vis antal ledige pladser» (OPG-0078), samme hjælper som de tre skema-widgets.
  const spotsMode = parseSpotsDisplay(initData.features?.spotsDisplay)
  // Which class type the visitor opened, its upcoming sessions, and the one
  // they picked to book. Keeping this inline is the whole point: "Book class"
  // used to navigate away to the tenant's own booking page.
  const [openType, setOpenType] = useState<ClassType | null>(null)
  const [sessions, setSessions] = useState<TypeSession[] | null>(null)
  const [sessionsError, setSessionsError] = useState<string | null>(null)
  const [selected, setSelected] = useState<TypeSession | null>(null)

  async function openSessions(ct: ClassType) {
    setOpenType(ct)
    setSessions(null)
    setSessionsError(null)
    const useAuth = authStore.isLoggedIn()
    let result = await apiClient.get<TypeSession[]>(`/api/widget/classes?templateId=${encodeURIComponent(ct.id)}`, useAuth)
    if (useAuth && result.code === 'AUTH_REQUIRED') {
      result = await apiClient.get<TypeSession[]>(`/api/widget/classes?templateId=${encodeURIComponent(ct.id)}`)
    }
    if (result.data) setSessions(result.data)
    else setSessionsError(result.error || str.loadError)
  }
  const [types, setTypes] = useState<ClassType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const slugsKey = holdTypeSlugs.join(',')
  const loadTypes = useCallback(async () => {
    const path = slugsKey
      ? `/api/widget/class-types?holdTypeSlugs=${encodeURIComponent(slugsKey)}`
      : '/api/widget/class-types'
    const result = await apiClient.get<ClassType[]>(path)
    if (result.data) setTypes(result.data)
    if (result.error) setError(result.error)
    setLoading(false)
  }, [slugsKey])

  useEffect(() => {
    loadTypes()
  }, [loadTypes])

  if (loading) {
    return (
      <div>
        <style>{styles}</style>
        <div class="hsw-ct-grid">
          {[0, 1, 2].map(i => (
            <div key={i} class="hsw-ct-card hsw-ct-skel-card">
              <div class="hsw-ct-skel hsw-ct-skel-img" />
              <div class="hsw-ct-skel-body">
                <div class="hsw-ct-skel hsw-ct-skel-title" />
                <div class="hsw-ct-skel hsw-ct-skel-meta" />
                <div class="hsw-ct-skel hsw-ct-skel-line" />
                <div class="hsw-ct-skel hsw-ct-skel-line" />
                <div class="hsw-ct-skel hsw-ct-skel-line" style={{ width: '70%' }} />
                <div class="hsw-ct-skel hsw-ct-skel-button" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <style>{styles}</style>
        <div role="alert" style={{ padding: '1rem 1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--yoga-radius, 0)', color: '#b91c1c', maxWidth: '600px', margin: '0 auto' }}>
          <strong>⚠️ {error}</strong>
        </div>
      </div>
    )
  }

  const visibleTypes = maxItems && maxItems > 0 ? types.slice(0, maxItems) : types

  if (!visibleTypes.length) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🧘</div>
        <p>{str.noClassTypes}</p>
      </div>
    )
  }

  return (
    <div>
      <style>{styles}</style>
      {title && <h2 class="hsw-ct-title-main">{title}</h2>}
      <div class="hsw-ct-grid">
        {visibleTypes.map(t => {
          const duration = t.duration ?? t.durationMinutes ?? null
          const benefits = (t.benefits ?? []).slice(0, 4)
          const metaParts: string[] = []
          if (duration) metaParts.push(`${duration} min`)
          if (t.level) metaParts.push(t.level)
          if (t.maxParticipants) metaParts.push(`max ${t.maxParticipants}`)

          return (
            <div key={t.id} class="hsw-ct-card">
              {t.imageUrl && (
                <div class="hsw-ct-img-wrap">
                  <img src={assetUrl(t.imageUrl)} alt={t.name} class="hsw-ct-img" loading="lazy" decoding="async" />
                </div>
              )}
              <div class="hsw-ct-body">
                <h3 class="hsw-ct-title">{t.name}</h3>

                {showMeta && metaParts.length > 0 && (
                  <div class="hsw-ct-meta">{metaParts.join(' · ')}</div>
                )}

                {t.description && (
                  <ExpandableDesc text={t.description} cls="hsw-ct-desc" lines={3} more={str.readMore} less={str.readLess} />
                )}

                {showBenefits && benefits.length > 0 && (
                  <ul class="hsw-ct-benefits">
                    {benefits.map((b, i) => (
                      <li key={i}><span class="hsw-ct-check">✓</span>{b}</li>
                    ))}
                  </ul>
                )}

                <div class="hsw-ct-foot">
                  {t.price != null && (() => {
                    const m = formatMoneyParts(t.price, initData.locale, initData.currency)
                    return (
                      <div class="hsw-ct-price">
                        {m.symbolFirst && <span class="hsw-ct-price-suffix">{m.symbol}</span>}
                        <span class="hsw-ct-price-amount">{m.amount}</span>
                        {!m.symbolFirst && <span class="hsw-ct-price-suffix">{m.symbol}</span>}
                      </div>
                    )
                  })()}
                  <button type="button" class="hsw-ct-cta" onClick={() => openSessions(t)}>
                    {str.bookClass}
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Sessions for the chosen type — booking happens in BookingModal right
          here, so the visitor never leaves the site the widget is embedded on. */}
      {openType && (
        <div class="hsw-ct-modal-backdrop" onClick={() => setOpenType(null)}>
          <div class="hsw-ct-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
            <button type="button" class="hsw-ct-modal-close" aria-label={str.close} onClick={() => setOpenType(null)}>×</button>
            <h3 class="hsw-ct-modal-title">{openType.name}</h3>
            {/* Hele beskrivelsen, også når kortet viste den forkortet. */}
            {openType.description && <p class="hsw-ct-modal-desc">{openType.description}</p>}
            <div class="hsw-ct-modal-sub">{str.upcomingSessions}</div>

            {sessionsError && <div class="hsw-ct-modal-error" role="alert">{sessionsError}</div>}
            {!sessions && !sessionsError && <div class="hsw-ct-modal-empty">…</div>}
            {sessions && sessions.length === 0 && (
              <div class="hsw-ct-modal-empty">{str.noClassesScheduled}</div>
            )}

            {sessions && sessions.length > 0 && (
              <ul class="hsw-ct-session-list">
                {sessions.map(sess => {
                  const left = sess.maxParticipants - sess.currentParticipants
                  const full = left <= 0
                  return (
                    <li key={sess.id}>
                      <button
                        type="button"
                        class="hsw-ct-session"
                        onClick={() => setSelected(sess)}
                      >
                        <span class="hsw-ct-session-when">
                          {/* I studiets tidszone, ikke den besøgendes browser (QA 27-10). */}
                          {fmtDag(sess.startTime, dateLocale, tz, { weekday: 'short', day: 'numeric', month: 'short' })}
                          {' · '}
                          {fmtTid(sess.startTime, dateLocale, tz)}
                        </span>
                        <span class="hsw-ct-session-spots">
                          {sess.isBooked ? `✓ ${str.booked}` : full ? (sess.waitlistEnabled ? str.waitlist : str.full) : widgetSpotsText(left, sess.maxParticipants, spotsMode, str, () => `${left} ${left === 1 ? str.spot : str.spots}`)}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {selected && (
        <BookingModal
          klass={{
            id: selected.id,
            name: selected.name,
            startTime: selected.startTime,
            endTime: selected.endTime,
            instructorName: selected.instructorName,
            room: selected.room,
            description: selected.description,
            maxParticipants: selected.maxParticipants,
            currentParticipants: selected.currentParticipants,
            dropInPriceDkk: selected.dropInPriceDkk,
            waitlistEnabled: selected.waitlistEnabled,
            membershipRequired: selected.membershipRequired,
            excludeFromMembership: selected.excludeFromMembership,
            isFree: selected.isFree,
            clipCost: selected.clipCost,
            classTemplateId: selected.classTemplateId,
            guestsAllowed: selected.guestsAllowed,
            isBooked: selected.isBooked,
          } as BookableClass}
          initData={initData}
          onClose={() => setSelected(null)}
          onBooked={() => { setSelected(null); setOpenType(null) }}
        />
      )}
    </div>
  )
}

const styles = `
${expandableDescStyles}
  button.hsw-ct-cta { border: none; cursor: pointer; font: inherit; }
  .hsw-ct-modal-backdrop {
    position: fixed; inset: 0; background: rgba(0,0,0,0.5);
    display: flex; align-items: center; justify-content: center; padding: 1rem; z-index: 9999;
  }
  .hsw-ct-modal {
    position: relative; background: #fff; border-radius: 16px; padding: 1.5rem;
    width: 100%; max-width: 460px; max-height: 90vh; overflow-y: auto;
    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  }
  .hsw-ct-modal-close {
    position: absolute; top: 0.75rem; right: 0.75rem; border: none; background: none;
    font-size: 1.5rem; line-height: 1; cursor: pointer; color: #737373;
  }
  .hsw-ct-modal-title { margin: 0 1.5rem 0.25rem 0; font-size: 1.25rem; }
  .hsw-ct-modal-desc { margin: 0.25rem 0 1rem; font-size: 0.9rem; line-height: 1.55; color: var(--yoga-text-muted, #525252); white-space: pre-line; overflow-wrap: anywhere; }
  .hsw-ct-modal-sub { font-size: 0.8125rem; text-transform: uppercase; letter-spacing: 0.06em; color: #737373; margin-bottom: 0.75rem; }
  .hsw-ct-modal-error { padding: 0.75rem; background: #fef2f2; color: #b91c1c; border-radius: 8px; font-size: 0.875rem; }
  .hsw-ct-modal-empty { padding: 1.5rem 0; text-align: center; color: #737373; }
  .hsw-ct-session-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
  .hsw-ct-session {
    width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 1rem;
    padding: 0.75rem 1rem; border: 1px solid #e5e5e5; border-radius: 10px;
    background: #fff; cursor: pointer; font: inherit; text-align: left;
  }
  .hsw-ct-session:hover { border-color: #a3a3a3; }
  .hsw-ct-session-when { font-weight: 600; }
  .hsw-ct-session-spots { font-size: 0.875rem; color: #737373; white-space: nowrap; }

  .hsw-ct-title-main {
    font-size: 2rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    text-align: center;
    margin: 0 auto 2rem;
    max-width: 1200px;
    letter-spacing: -0.01em;
  }
  .hsw-ct-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    max-width: 1200px;
    margin: 0 auto;
  }
  .hsw-ct-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all .3s;
  }
  .hsw-ct-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    transform: translateY(-2px);
  }
  .hsw-ct-img-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #f5f5f5;
    overflow: hidden;
  }
  .hsw-ct-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .hsw-ct-body {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .hsw-ct-title {
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
    line-height: 1.3;
  }
  .hsw-ct-meta {
    font-size: 0.8rem;
    color: var(--yoga-text-muted, #737373);
    margin: 0 0 0.85rem;
    text-transform: lowercase;
    letter-spacing: 0.02em;
  }
  .hsw-ct-desc {
    font-size: 0.9rem;
    color: var(--yoga-text-muted, #525252);
    line-height: 1.55;
    margin: 0;
  }
  .hsw-ct-benefits {
    list-style: none;
    padding: 0;
    margin: 0 0 1.25rem;
  }
  .hsw-ct-benefits li {
    padding: 0.4rem 0 0.4rem 1.5rem;
    position: relative;
    font-size: 0.85rem;
    color: var(--yoga-text, #404040);
    line-height: 1.4;
  }
  .hsw-ct-check {
    position: absolute;
    left: 0;
    color: var(--yoga-primary, #FF6B35);
    font-weight: 700;
    font-size: 0.95rem;
  }
  .hsw-ct-foot {
    margin-top: auto;
    padding-top: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .hsw-ct-price {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
    line-height: 1;
  }
  .hsw-ct-price-amount {
    font-size: 1.6rem;
    font-weight: 800;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.03em;
  }
  .hsw-ct-price-suffix {
    font-size: 0.8rem;
    color: var(--yoga-text-muted, #737373);
    font-weight: 500;
  }
  .hsw-ct-cta {
    display: inline-block;
    padding: 0.7rem 1.25rem;
    text-align: center;
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    font-weight: 500;
    border: 1px solid #d4d4d4;
    border-radius: var(--yoga-radius, 0);
    text-decoration: none;
    font-size: 0.85rem;
    letter-spacing: 0.01em;
    transition: all .3s;
    margin-left: auto;
  }
  .hsw-ct-cta:hover {
    background: var(--yoga-text, #0a0a0a);
    color: var(--yoga-card-bg, #fff);
    border-color: var(--yoga-text, #0a0a0a);
  }
  /* Skeletons */
  .hsw-ct-skel-card { overflow: hidden; }
  .hsw-ct-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-ct-shimmer 1.5s infinite;
    border-radius: 4px;
  }
  .hsw-ct-skel-img {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 0;
  }
  .hsw-ct-skel-body {
    padding: 1.5rem;
  }
  .hsw-ct-skel-title { height: 22px; width: 65%; margin-bottom: 0.75rem; }
  .hsw-ct-skel-meta { height: 12px; width: 45%; margin-bottom: 1rem; }
  .hsw-ct-skel-line { height: 14px; width: 100%; margin-bottom: 0.5rem; }
  .hsw-ct-skel-button { height: 38px; width: 100%; margin-top: 1rem; }
  @keyframes hsw-ct-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`
